import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSpeech } from "@/hooks/useSpeech";
import { Volume2, X, Sun, Square } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const todayKey = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? "Günaydın" : h < 18 ? "İyi günler" : "İyi akşamlar";
};

export function buildBriefing(appts) {
    const today = todayKey();
    const list = appts
        .filter((a) => a.status === "scheduled" && a.start)
        .filter((a) => {
            const d = new Date(a.start);
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` === today;
        })
        .sort((a, b) => a.start.localeCompare(b.start));
    if (!list.length) return { count: 0, text: `${greeting()}! Bugün planlanmış randevunuz yok.` };
    const items = list.map((a) => {
        const d = new Date(a.start);
        return `${pad(d.getHours())}:${pad(d.getMinutes())} ${a.patient_name || "hasta"}${a.title ? `, ${a.title}` : ""}`;
    });
    return { count: list.length, text: `${greeting()}! Bugün ${list.length} randevunuz var. ${items.join(". ")}.` };
}

export const MorningBriefing = () => {
    const { user } = useAuth();
    const speech = useSpeech();
    const [brief, setBrief] = useState(null);
    const storeKey = `dd_brief_${user?.id || user?.email}`;
    const isDesktop = !!window.dentdefter?.isDesktop;

    useEffect(() => {
        if (localStorage.getItem(storeKey) === todayKey()) return;
        api.get("/appointments")
            .then((r) => {
                const b = buildBriefing(r.data);
                if (isDesktop && speech.supported) {
                    localStorage.setItem(storeKey, todayKey());
                    setTimeout(() => speech.speak("brief", b.text), 1500);
                }
                setBrief(b);
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeKey]);

    const close = () => {
        localStorage.setItem(storeKey, todayKey());
        speech.stop();
        setBrief(null);
    };

    if (!brief) return null;
    const speaking = speech.speakingId === "brief";
    return (
        <div className="fixed z-40 top-4 left-4 right-4 sm:top-auto sm:bottom-6 sm:right-auto sm:left-72 max-w-sm rounded-2xl border border-amber-200 bg-white/95 backdrop-blur-xl shadow-xl p-4 dd-ai-pop no-print" data-testid="morning-briefing">
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
                    <Sun className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900">Günün randevu özeti</div>
                    <p className="text-xs text-slate-600 mt-1 max-h-24 overflow-y-auto" data-testid="morning-briefing-text">{brief.text}</p>
                    {speech.supported && (
                        <button
                            onClick={() => (speaking ? speech.stop() : speech.speak("brief", brief.text))}
                            className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#0F4C5C] text-white hover:bg-[#00A896] transition-colors"
                            data-testid="morning-briefing-listen-btn"
                        >
                            {speaking ? <Square className="w-3 h-3" /> : <Volume2 className="w-3.5 h-3.5" />}
                            {speaking ? "Durdur" : "Dinle"}
                        </button>
                    )}
                </div>
                <button onClick={close} className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-testid="morning-briefing-close-btn" title="Kapat">
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};
